import { createConfig } from "./config.js";

/**
 * Debug utilities for Verified User Tracking
 */

let debugMode = false;

/**
 * Initialize debug mode based on config and URL parameters
 */
export function initDebugMode(config) {
  const urlParams = new URLSearchParams(window.location.search);
  debugMode = urlParams.get("debug") === "1" || config?.debug === true;

  if (debugMode) {
    log("Debug mode enabled", "info");
    log("Configuration:", "info", config);
  }

  return debugMode;
}

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = () => debugMode;

/**
 * Log debug message if debug mode is enabled
 */
export function log(message, level = "log", data = undefined) {
  if (!debugMode) return;

  const prefix = "🔍 [VerifiedTracking]";
  const method = ["info", "warn", "error"].includes(level) ? level : "log";

  console[method](prefix, message, ...(data !== undefined ? [data] : []));
  addDebugMessage(message, level);
}

/**
 * Create debug UI panel if debug mode is enabled
 */
export function createDebugPanel() {
  if (!debugMode) return;

  const config = createConfig(window.VerifiedTrackingConfig || {});

  // Add external CSS
  const styleId = "verified-tracking-debug-css";
  if (!document.getElementById(styleId)) {
    const style = Object.assign(document.createElement("link"), {
      id: styleId,
      rel: "stylesheet",
      type: "text/css",
      href: "../assets/debug-panel.css",
    });
    document.head.appendChild(style);
  }

  // Create panel structure
  const panel = createElement("div", "vtd", null, null, ["opened"]);
  createElement("div", "vtd-button", null, panel);
  createElement("div", "vtd-header", "Trackxact", panel);

  // Settings section
  const settingsSection = createElement("div", "vtd-settings", null, panel);
  createElement("div", "vtd-settings-header", "Settings", settingsSection);

  const settingsData = [
    {
      id: "time-threshold",
      label: "Time Threshold",
      value: `${config.minTimeOnPageSeconds || 0} Seconds`,
    },
    {
      id: "scroll-threshold",
      label: "Scroll Threshold",
      value: `${config.minScrollDepthPercent || 0} %`,
    },
  ];

  settingsData.forEach(({ id, label, value }) => {
    const row = createElement(
      "div",
      `vtd-settings-${id}`,
      null,
      settingsSection,
      ["bg-green", "settings-cells"]
    );
    createElement("div", `vtd-settings-${id}-key`, label, row);
    createElement("div", `vtd-settings-${id}-value`, value, row);
  });

  // Session section
  const sessionSection = createElement("div", "vtd-session", null, panel);
  createElement("div", "vtd-session-header", "Current Session", sessionSection);

  const sessionData = [
    {
      id: "tracking-status",
      label: "Tracking Status",
      value: config ? "Activated" : "Deactivated",
      color: "bg-green",
    },
    {
      id: "consent-status",
      label: "Consent Status",
      value: config.storageKeys.consentGiven ? "Granted" : "Dismissed",
      color: "bg-green",
    },
    {
      id: "total-time",
      label: "Total Engagement Time",
      value: "0",
      color: "bg-green",
    },
    {
      id: "total-scroll",
      label: "Total Scroll Depth",
      value: "0",
      color: "bg-purple",
    },
    {
      id: "event-status",
      label: "Event Status",
      value: "Waiting",
      color: "bg-purple",
    },
  ];

  sessionData.forEach(({ id, label, value, color }) => {
    const row = createElement(
      "div",
      `vtd-session-${id}`,
      null,
      sessionSection,
      [color, "settings-cells"]
    );
    createElement("div", `vtd-session-${id}-key`, label, row);
    createElement("div", `vtd-session-${id}-value`, value, row);
  });

  // Content section
  createElement("div", "vtd-content-header", "Current Session Log", panel);
  createElement("div", "vtd-content", null, panel);

  document.body.appendChild(panel);

  // Toggle button handler
  document.getElementById("vtd-button").addEventListener("click", () => {
    panel.className = panel.classList.contains("opened") ? "closed" : "opened";
  });

  addDebugMessage("Debug panel initialized");
}

/**
 * Add message to debug panel
 */
export function addDebugMessage(message, type = "info") {
  if (!debugMode) return;

  const content = document.getElementById("vtd-content");
  if (!content) return;

  const entry = createElement("div", null, null, null, [
    "bg-neutral",
    "settings-cells",
  ]);
  const time = new Date().toTimeString().split(" ")[0];
  entry.innerHTML = `<span>[${time}]</span> <span>${message}</span>`;

  content.appendChild(entry);
  content.scrollTop = content.scrollHeight;
}

export function sendExternalLogs(data) {
  fetch("https://s1471113.eu-nbg-2.betterstackdata.com", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer bEpi8w5p9wUqtWMPbTarXXX4",
    },
    body: JSON.stringify({
      dt: new Date().toISOString().replace("T", " ").replace("Z", " UTC"),
      message: data,
    }),
  });
}

function createElement(tag, id, textContent, parent, classNames = []) {
  const element = document.createElement(tag);
  if (id) element.id = id;
  classNames.forEach((className) => element.classList.add(className));
  if (textContent) element.textContent = textContent;
  if (parent) parent.appendChild(element);
  return element;
}

export function updateDebugPanelCell(id, text = null, classNames = []) {
  const element = document.getElementById(id);
  if (element?.parentElement) {
    element.parentElement.className = "settings-cells";
    classNames.forEach((className) =>
      element.parentElement.classList.add(className)
    );
  }
  if (text) element.textContent = text;
}